import { MetaData, FilterCategory } from 'uiComponents/filter/schema'
import { formatFlatOptions } from 'uiComponents/filter/filterHelpers'
import { t } from 'translations'

interface ApplicableFiltersConfig {
    orders: FilterCategory[]
    tickets: FilterCategory[]
    ordersBeta: FilterCategory[]
}

export const applicableFilters: ApplicableFiltersConfig = {
    orders: ['products', 'payment_method', 'channels', 'language', 'location_id', 'email_status'],
    tickets: ['products', 'payment_method', 'channels', 'language'],
    ordersBeta: ['products', 'payment_method', 'channels', 'location_id', 'status', 'email_status'],
}

// Labels are getters so they are translated when read, not when this module loads.
const categoryOptions = {
    payment_method: [
        { name: 'Amex', slug: 'amex' },
        { name: 'Bancontact', slug: 'bancontact' },
        {
            get name() {
                return t('orders.ordersFilterConfig.paymentMethod.banktransfer')
            },
            slug: 'banktransfer',
        },
        { name: 'BCMC', slug: 'bcmc' },
        { name: 'Belfius', slug: 'belfius' },
        { name: 'Convious', slug: 'convious' },
        {
            get name() {
                return t('orders.ordersFilterConfig.paymentMethod.creditcard')
            },
            slug: 'creditcard',
        },
        { name: 'Direct E-Banking', slug: 'directEbanking' },
        { name: 'EBanking FI', slug: 'ebanking_FI' },
        { name: 'Entercash', slug: 'entercash' },
        { name: 'EPS', slug: 'eps' },
        {
            get name() {
                return t('orders.ordersFilterConfig.paymentMethod.giftcard')
            },
            slug: 'giftcard',
        },
        { name: 'Giropay', slug: 'giropay' },
        { name: 'Ideal', slug: 'ideal' },
        { name: 'Inghomepay', slug: 'inghomepay' },
        { name: 'KBC', slug: 'kbc' },
        { name: 'MC', slug: 'mc' },
        { name: 'Paypal', slug: 'paypal' },
        { name: 'Przelewy24', slug: 'przelewy24' },
        { name: 'Sofort', slug: 'sofort' },
        { name: 'Visa', slug: 'visa' },
    ],
    email_status: [
        {
            get name() {
                return t('orders.ordersFilterConfig.emailStatus.sending')
            },
            slug: 'sending',
        },
        {
            get name() {
                return t('orders.ordersFilterConfig.emailStatus.processed')
            },
            slug: 'processed',
        },
        {
            get name() {
                return t('orders.ordersFilterConfig.emailStatus.delivered')
            },
            slug: 'delivered',
        },
        {
            get name() {
                return t('orders.ordersFilterConfig.emailStatus.opened')
            },
            slug: 'opened',
        },
        {
            get name() {
                return t('orders.ordersFilterConfig.emailStatus.clicked')
            },
            slug: 'clicked',
        },
        {
            get name() {
                return t('orders.ordersFilterConfig.emailStatus.deferred')
            },
            slug: 'deferred',
        },
        {
            get name() {
                return t('orders.ordersFilterConfig.emailStatus.bounced')
            },
            slug: 'bounced',
        },
        {
            get name() {
                return t('orders.ordersFilterConfig.emailStatus.dropped')
            },
            slug: 'dropped',
        },
    ],
    ticket_status: [
        {
            get name() {
                return t('orders.ordersFilterConfig.ticketStatus.paid')
            },
            slug: 'paid',
        },
        {
            get name() {
                return t('orders.ordersFilterConfig.ticketStatus.refunded')
            },
            slug: 'refunded',
        },
        {
            get name() {
                return t('orders.ordersFilterConfig.ticketStatus.chargedBack')
            },
            slug: 'charged_back',
        },
        {
            get name() {
                return t('orders.ordersFilterConfig.ticketStatus.redeemed')
            },
            slug: 'redeemed',
        },
    ],
    apf_status: [
        {
            get name() {
                return t('orders.ordersFilterConfig.apfStatus.apfCompleted')
            },
            slug: 'apf_completed',
        },
        {
            get name() {
                return t('orders.ordersFilterConfig.apfStatus.apfMissing')
            },
            slug: 'apf_missing',
        },
    ],
    redeem_status: [
        {
            get name() {
                return t('orders.ordersFilterConfig.redeemStatus.redeemed')
            },
            slug: 'redeemed',
        },
        {
            get name() {
                return t('orders.ordersFilterConfig.redeemStatus.notRedeemed')
            },
            slug: 'not_redeemed',
        },
    ],
    print_status: [
        {
            get name() {
                return t('orders.ordersFilterConfig.printStatus.printed')
            },
            slug: 'printed',
        },
        {
            get name() {
                return t('orders.ordersFilterConfig.printStatus.notPrinted')
            },
            slug: 'not_printed',
        },
    ],
    status: [
        {
            get name() {
                return t('orders.ordersFilterConfig.status.confirmed')
            },
            slug: 'CONFIRMED',
        },
        {
            get name() {
                return t('orders.ordersFilterConfig.status.canceled')
            },
            slug: 'CANCELED',
        },
    ],
}

export const getStaticMetadaCategories = (): MetaData[] => [
    {
        category: 'payment_method' as FilterCategory,
        options: formatFlatOptions('payment_method', categoryOptions.payment_method),
    },
    {
        category: 'email_status' as FilterCategory,
        options: formatFlatOptions('email_status', categoryOptions.email_status),
    },
    {
        category: 'ticket_status' as FilterCategory,
        options: formatFlatOptions('ticket_status', categoryOptions.ticket_status),
    },
    {
        category: 'apf_status' as FilterCategory,
        options: formatFlatOptions('apf_status', categoryOptions.apf_status),
    },
    {
        category: 'redeem_status' as FilterCategory,
        options: formatFlatOptions('redeem_status', categoryOptions.redeem_status),
    },
    {
        category: 'print_status' as FilterCategory,
        options: formatFlatOptions('print_status', categoryOptions.print_status),
    },
    {
        category: 'status' as FilterCategory,
        options: formatFlatOptions('status', categoryOptions.status),
    },
]
